import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Upload, Trash2, Monitor, Tablet, Smartphone, Plus, RefreshCw, CheckCircle2, XCircle, Loader2, Save, Link2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : null);

const SOURCE_FIELDS = ["product_name", "workspace_name", "tagline", "logo_url", "logo_position", "logo_alignment", "branding_config", "user_email"];

export default function BrandingCRM() {
  const [branding, setBranding] = useState(null);
  const [crm, setCrm] = useState(null);
  const [oauth, setOauth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c, o] = await Promise.all([
        api.get("/branding"), api.get("/branding/crm"), api.get("/branding/oauth"),
      ]);
      setBranding(b.data); setCrm(c.data); setOauth(o.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load branding config");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !branding) {
    return <div className="flex items-center gap-2 text-slate-500 p-8"><Loader2 className="animate-spin" size={18} /> Loading configuration…</div>;
  }

  return (
    <div data-testid="branding-crm-module">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Branding &amp; CRM Setup</h2>
        <p className="text-sm text-slate-500">Configure logo &amp; branding, login page, preview changes, then save or sync to CRM.</p>
      </div>
      <Tabs defaultValue="logo">
        <TabsList className="mb-4">
          <TabsTrigger value="logo" data-testid="bc-tab-logo">Logo &amp; Branding</TabsTrigger>
          <TabsTrigger value="login" data-testid="bc-tab-login">Login Page</TabsTrigger>
          <TabsTrigger value="preview" data-testid="bc-tab-preview">Preview</TabsTrigger>
          <TabsTrigger value="crm" data-testid="bc-tab-crm">CRM Sync</TabsTrigger>
        </TabsList>
        <TabsContent value="logo"><LogoBranding branding={branding} setBranding={setBranding} onSaved={load} /></TabsContent>
        <TabsContent value="login"><LoginConfig branding={branding} setBranding={setBranding} oauth={oauth} setOauth={setOauth} onSaved={load} /></TabsContent>
        <TabsContent value="preview"><Preview branding={branding} oauth={oauth} /></TabsContent>
        <TabsContent value="crm"><CrmSync crm={crm} setCrm={setCrm} onSaved={load} /></TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children, htmlFor }) {
  return <div className="space-y-1.5"><Label htmlFor={htmlFor}>{label}</Label>{children}</div>;
}

// ---------------- Logo & Branding ----------------
function LogoBranding({ branding, setBranding, onSaved }) {
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setBranding((b) => ({ ...b, [k]: v }));
  const setResp = (k, v) => setBranding((b) => ({ ...b, responsive: { ...(b.responsive || {}), [k]: Number(v) } }));

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const ok = ["image/svg+xml", "image/png", "image/jpeg", "image/jpg"];
    if (!ok.includes(file.type)) { toast.error("Use SVG, PNG or JPG"); e.target.value = ""; return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBranding((b) => ({ ...b, logo_url: r.data.logo_url, logo_filename: r.data.logo_filename, logo_mime: r.data.logo_mime }));
      toast.success("Logo uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  const removeLogo = async () => {
    try { await api.delete("/branding/logo"); setBranding((b) => ({ ...b, logo_url: null, logo_filename: null })); toast.success("Logo removed"); }
    catch { toast.error("Failed to remove"); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        product_name: branding.product_name, tagline: branding.tagline,
        logo_width: Number(branding.logo_width), logo_height: Number(branding.logo_height),
        position: branding.position, margin: Number(branding.margin || 0), padding: Number(branding.padding || 0),
        responsive: branding.responsive,
      };
      const r = await api.put("/branding", payload);
      setBranding(r.data);
      toast.success("Branding saved");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-slate-800">Logo</h3>
        <div className="flex items-center gap-4">
          <div className="h-24 w-24 rounded-lg border bg-slate-50 flex items-center justify-center overflow-hidden">
            {branding.logo_url
              ? <img src={absUrl(branding.logo_url)} alt="logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview-img" />
              : <span className="text-xs text-slate-400">No logo</span>}
          </div>
          <div className="space-y-2">
            <input ref={fileRef} type="file" accept=".svg,.png,.jpg,.jpeg" className="hidden" onChange={upload} data-testid="logo-file-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} data-testid="logo-upload-btn"><Upload size={15} className="mr-1.5" /> {branding.logo_url ? "Replace" : "Upload"}</Button>
            {branding.logo_url && <Button size="sm" variant="outline" className="text-red-600" onClick={removeLogo} data-testid="logo-remove-btn"><Trash2 size={15} className="mr-1.5" /> Remove</Button>}
            <p className="text-xs text-slate-400">SVG, PNG, JPG · max 3MB</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Width (px)"><Input type="number" value={branding.logo_width || ""} onChange={(e) => set("logo_width", e.target.value)} data-testid="logo-width" /></Field>
          <Field label="Height (px)"><Input type="number" value={branding.logo_height || ""} onChange={(e) => set("logo_height", e.target.value)} data-testid="logo-height" /></Field>
          <Field label="Position">
            <Select value={branding.position} onValueChange={(v) => set("position", v)}>
              <SelectTrigger data-testid="logo-position"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Margin"><Input type="number" value={branding.margin || 0} onChange={(e) => set("margin", e.target.value)} data-testid="logo-margin" /></Field>
            <Field label="Padding"><Input type="number" value={branding.padding || 0} onChange={(e) => set("padding", e.target.value)} data-testid="logo-padding" /></Field>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-400">Responsive heights (px)</Label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1.5">
            {["desktop", "tablet", "mobile"].map((d) => (
              <Field key={d} label={d[0].toUpperCase() + d.slice(1)}><Input type="number" value={branding.responsive?.[d] || ""} onChange={(e) => setResp(d, e.target.value)} data-testid={`resp-${d}`} /></Field>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-slate-800">Branding</h3>
        <Field label="Product Name"><Input value={branding.product_name || ""} onChange={(e) => set("product_name", e.target.value)} data-testid="brand-name" /></Field>
        <Field label="Tagline / Subtitle"><Input value={branding.tagline || ""} onChange={(e) => set("tagline", e.target.value)} data-testid="brand-tagline" /></Field>
        {branding.updated_by && <p className="text-xs text-slate-400">Last saved by {branding.updated_by} · {branding.updated_at ? new Date(branding.updated_at).toLocaleString() : ""}</p>}
        <Button onClick={save} disabled={saving} data-testid="save-branding-btn"><Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save Configuration"}</Button>
      </div>
    </div>
  );
}

// ---------------- Login Page config ----------------
const FONT_OPTIONS = ["", "Inter, sans-serif", "Georgia, serif", "Arial, sans-serif", "Verdana, sans-serif", "system-ui, sans-serif", "Courier New, monospace"];
const WEIGHT_OPTIONS = ["400", "500", "600", "700", "800"];

function ColorField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <div className="flex gap-1.5">
        <Input type="color" className="w-10 p-1 h-9 shrink-0" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        <Input className="h-9" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}

function LoginConfig({ branding, setBranding, oauth, setOauth, onSaved }) {
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setBranding((b) => ({ ...b, [k]: v }));
  const setProv = (name, k, v) => setOauth((o) => ({ ...o, providers: { ...o.providers, [name]: { ...o.providers[name], [k]: v } } }));
  const texts = branding.texts || {};
  const styles = branding.styles || {};
  const setText = (k, v) => setBranding((b) => ({ ...b, texts: { ...(b.texts || {}), [k]: v } }));
  const setStyle = (k, v) => setBranding((b) => ({ ...b, styles: { ...(b.styles || {}), [k]: v } }));
  const setFeature = (i, k, v) => setBranding((b) => {
    const feats = [...((b.texts || {}).features || [])];
    feats[i] = { ...feats[i], [k]: v };
    return { ...b, texts: { ...(b.texts || {}), features: feats } };
  });

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all([
        api.put("/branding", {
          login_background: branding.login_background, login_alignment: branding.login_alignment,
          login_panel_placement: branding.login_panel_placement, login_button_color: branding.login_button_color,
          login_subtitle: branding.login_subtitle, texts: branding.texts, styles: branding.styles,
        }),
        api.put("/branding/oauth", { providers: oauth.providers }),
      ]);
      toast.success("Login design saved");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset all login page design settings to defaults? Logo, product name and tagline are kept.")) return;
    try { const r = await api.post("/branding/reset"); setBranding(r.data); toast.success("Reset to defaults"); onSaved?.(); }
    catch { toast.error("Reset failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Appearance */}
        <div className="bg-white border rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-slate-800">Appearance</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Background Color"><div className="flex gap-2"><Input type="color" className="w-14 p-1 h-10" value={branding.login_background || "#0B1120"} onChange={(e) => set("login_background", e.target.value)} data-testid="login-bg" /><Input value={branding.login_background || ""} onChange={(e) => set("login_background", e.target.value)} /></div></Field>
            <Field label="Button Color"><div className="flex gap-2"><Input type="color" className="w-14 p-1 h-10" value={branding.login_button_color || "#2563EB"} onChange={(e) => set("login_button_color", e.target.value)} data-testid="login-btn-color" /><Input value={branding.login_button_color || ""} onChange={(e) => set("login_button_color", e.target.value)} /></div></Field>
            <Field label="Content Alignment">
              <Select value={branding.login_alignment} onValueChange={(v) => set("login_alignment", v)}>
                <SelectTrigger data-testid="login-align"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem></SelectContent>
              </Select>
            </Field>
            <Field label="Form Panel Placement">
              <Select value={branding.login_panel_placement} onValueChange={(v) => set("login_panel_placement", v)}>
                <SelectTrigger data-testid="login-panel"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="right">Right</SelectItem><SelectItem value="left">Left</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Font Family</Label>
            <Select value={styles.font_family || "default"} onValueChange={(v) => setStyle("font_family", v === "default" ? "" : v)}>
              <SelectTrigger data-testid="style-font"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Default (inherit)</SelectItem>
                {FONT_OPTIONS.filter(Boolean).map((f) => <SelectItem key={f} value={f}>{f.split(",")[0]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Style controls */}
        <div className="bg-white border rounded-xl p-5 space-y-4">
          <h3 className="font-semibold text-slate-800">Text Styles</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-1"><Label className="text-xs">Title size</Label><Input type="number" className="h-9" value={styles.title_size || ""} onChange={(e) => setStyle("title_size", Number(e.target.value))} data-testid="style-title-size" /></div>
            <div className="space-y-1"><Label className="text-xs">Title weight</Label>
              <Select value={String(styles.title_weight || "700")} onValueChange={(v) => setStyle("title_weight", v)}>
                <SelectTrigger className="h-9" data-testid="style-title-weight"><SelectValue /></SelectTrigger>
                <SelectContent>{WEIGHT_OPTIONS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Title line-height</Label><Input type="number" step="0.05" min="1" max="2.5" className="h-9" value={styles.title_line_height || ""} onChange={(e) => setStyle("title_line_height", Number(e.target.value))} /></div>
            <div className="space-y-1"><Label className="text-xs">Desc size</Label><Input type="number" className="h-9" value={styles.desc_size || ""} onChange={(e) => setStyle("desc_size", Number(e.target.value))} data-testid="style-desc-size" /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <ColorField label="Title color" value={styles.title_color} onChange={(v) => setStyle("title_color", v)} testid="style-title-color" />
            <ColorField label="Description color" value={styles.desc_color} onChange={(v) => setStyle("desc_color", v)} testid="style-desc-color" />
            <ColorField label="Brand name color" value={styles.brand_name_color} onChange={(v) => setStyle("brand_name_color", v)} testid="style-brand-color" />
            <ColorField label="Tagline color" value={styles.tagline_color} onChange={(v) => setStyle("tagline_color", v)} testid="style-tagline-color" />
            <ColorField label="Sign-in title color" value={styles.signin_title_color} onChange={(v) => setStyle("signin_title_color", v)} testid="style-signin-color" />
            <div className="space-y-1"><Label className="text-xs">Desc line-height</Label><Input type="number" step="0.05" min="1" max="2.5" className="h-9" value={styles.desc_line_height || ""} onChange={(e) => setStyle("desc_line_height", Number(e.target.value))} /></div>
          </div>
        </div>
      </div>

      {/* Left panel content */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-slate-800">Left Panel Content</h3>
        <Field label="Main Title"><Input value={texts.main_title || ""} onChange={(e) => setText("main_title", e.target.value)} data-testid="text-main-title" /></Field>
        <Field label="Description"><textarea className="w-full border rounded-md p-2 text-sm" rows={2} value={texts.description || ""} onChange={(e) => setText("description", e.target.value)} data-testid="text-description" /></Field>
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wide text-slate-400">Feature Cards</Label>
          {(texts.features || []).map((f, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input placeholder="Title" value={f.title || ""} onChange={(e) => setFeature(i, "title", e.target.value)} data-testid={`feature-title-${i}`} />
              <Input placeholder="Description" value={f.desc || ""} onChange={(e) => setFeature(i, "desc", e.target.value)} data-testid={`feature-desc-${i}`} />
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Footer Text"><Input value={texts.footer_text || ""} onChange={(e) => setText("footer_text", e.target.value)} data-testid="text-footer" /></Field>
          <Field label="Powered By Text"><Input value={texts.powered_by_text || ""} onChange={(e) => setText("powered_by_text", e.target.value)} data-testid="text-powered-by" /></Field>
          <Field label="Copyright Text"><Input value={texts.copyright_text || ""} onChange={(e) => setText("copyright_text", e.target.value)} data-testid="text-copyright" /></Field>
        </div>
      </div>

      {/* Sign-in form text */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-slate-800">Sign-in Form Text</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Sign-in Title"><Input value={texts.signin_title || ""} onChange={(e) => setText("signin_title", e.target.value)} data-testid="text-signin-title" /></Field>
          <Field label="Subtitle"><Input value={branding.login_subtitle || ""} onChange={(e) => set("login_subtitle", e.target.value)} data-testid="login-subtitle" /></Field>
          <Field label="Button Text"><Input value={texts.signin_button_text || ""} onChange={(e) => setText("signin_button_text", e.target.value)} data-testid="text-signin-btn" /></Field>
          <Field label="Email Label"><Input value={texts.email_label || ""} onChange={(e) => setText("email_label", e.target.value)} data-testid="text-email-label" /></Field>
          <Field label="Email Placeholder"><Input value={texts.email_placeholder || ""} onChange={(e) => setText("email_placeholder", e.target.value)} /></Field>
          <Field label="Password Label"><Input value={texts.password_label || ""} onChange={(e) => setText("password_label", e.target.value)} /></Field>
          <Field label="Password Placeholder"><Input value={texts.password_placeholder || ""} onChange={(e) => setText("password_placeholder", e.target.value)} /></Field>
          <Field label="OAuth Button Text ({'{provider}'} auto)"><Input value={texts.oauth_button_text || ""} onChange={(e) => setText("oauth_button_text", e.target.value)} /></Field>
          <Field label="Forgot Password Text"><Input value={texts.forgot_password_text || ""} onChange={(e) => setText("forgot_password_text", e.target.value)} data-testid="text-forgot" /></Field>
        </div>
        <p className="text-xs text-slate-400">Email/password sign-in stays enabled. Logo, product name &amp; tagline come from the Logo &amp; Branding tab.</p>
      </div>

      {/* OAuth providers */}
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <h3 className="font-semibold text-slate-800">OAuth Providers <span className="text-xs font-normal text-slate-400">(shown on login only when configured)</span></h3>
        {oauth && ["google", "microsoft", "oidc"].map((name) => {
          const p = oauth.providers[name];
          return (
            <div key={name} className="border rounded-lg p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium capitalize text-sm">{name === "oidc" ? "Other OIDC" : name}</span>
                <Switch checked={!!p.enabled} onCheckedChange={(v) => setProv(name, "enabled", v)} data-testid={`oauth-${name}-enabled`} />
              </div>
              {p.enabled && (
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Client ID" value={p.client_id || ""} onChange={(e) => setProv(name, "client_id", e.target.value)} data-testid={`oauth-${name}-client-id`} />
                  <Input placeholder={p.has_client_secret ? "•••••• (saved — leave blank to keep)" : "Client Secret"} type="password" value={p.client_secret || ""} onChange={(e) => setProv(name, "client_secret", e.target.value)} data-testid={`oauth-${name}-client-secret`} />
                  <Input placeholder="Authorization URL" value={p.authorization_url || ""} onChange={(e) => setProv(name, "authorization_url", e.target.value)} />
                  <Input placeholder="Token URL" value={p.token_url || ""} onChange={(e) => setProv(name, "token_url", e.target.value)} />
                  <Input placeholder="Redirect URI" value={p.redirect_uri || ""} onChange={(e) => setProv(name, "redirect_uri", e.target.value)} />
                  <Input placeholder="Scopes" value={p.scopes || ""} onChange={(e) => setProv(name, "scopes", e.target.value)} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={save} disabled={saving} data-testid="save-login-btn"><Save size={15} className="mr-1.5" /> {saving ? "Saving…" : "Save Login Design"}</Button>
        <Button variant="outline" className="text-red-600" onClick={resetDefaults} data-testid="reset-login-btn">Reset to Default</Button>
      </div>
    </div>
  );
}

// ---------------- Preview ----------------
function Preview({ branding, oauth }) {
  const [device, setDevice] = useState("desktop");
  const widths = { desktop: "100%", tablet: 900, mobile: 390 };
  const providers = oauth ? Object.entries(oauth.providers).filter(([, p]) => p.enabled && p.client_id).map(([n]) => n) : [];
  const texts = branding.texts || {};
  const styles = branding.styles || {};
  const logoH = branding.responsive?.[device] || branding.logo_height || 48;
  const justify = branding.position === "center" ? "center" : branding.position === "right" ? "flex-end" : "flex-start";
  const panelRight = (branding.login_panel_placement || "right") === "right";
  const stack = device === "mobile";
  const center = branding.login_alignment === "center";
  const font = styles.font_family || undefined;

  const BrandPanel = (
    <div className="flex-1 p-8 text-white flex flex-col justify-between" style={{ background: branding.login_background || "#0B1120", fontFamily: font }}>
      <div style={{ display: "flex", justifyContent: justify }}>
        {branding.logo_url
          ? <img src={absUrl(branding.logo_url)} alt="logo" style={{ height: logoH, objectFit: "contain" }} />
          : <div className="h-12 w-12 rounded-lg bg-blue-600 flex items-center justify-center font-bold">TM</div>}
      </div>
      <div style={{ textAlign: center ? "center" : "left" }}>
        <div className="font-bold" style={{ color: styles.title_color || "#fff", fontSize: Math.min(styles.title_size || 44, device === "mobile" ? 22 : 34), fontWeight: styles.title_weight || 700, lineHeight: styles.title_line_height || 1.15 }}>{texts.main_title || "Organizational Execution Operating System"}</div>
        <div className="mt-3" style={{ color: styles.desc_color || "#94A3B8", fontSize: Math.min(styles.desc_size || 18, 15), lineHeight: styles.desc_line_height || 1.6 }}>{texts.description}</div>
        <div className={`mt-5 grid gap-2 ${stack ? "grid-cols-1" : "grid-cols-3"}`}>
          {(texts.features || []).map((f, i) => (
            <div key={i} className="rounded-lg border border-slate-700/60 bg-white/5 p-3" style={{ textAlign: center ? "center" : "left" }}>
              <div className="font-semibold text-xs">{f.title}</div>
              <div className="text-[10px] mt-0.5" style={{ color: styles.desc_color || "#94A3B8" }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[10px] text-slate-500">{texts.footer_text}</div>
    </div>
  );
  const FormPanel = (
    <div className="flex-1 bg-white p-8 flex flex-col justify-center" style={{ fontFamily: font }}>
      <div className="text-lg font-bold" style={{ color: styles.signin_title_color || "#0F172A" }}>{texts.signin_title || "Sign in"}</div>
      <div className="text-xs text-slate-500 mb-4">{branding.login_subtitle || "Enter your credentials to continue"}</div>
      <div className="space-y-2 max-w-xs">
        <div className="text-xs text-slate-500">{texts.email_label || "Email"}</div>
        <div className="h-9 rounded-md border bg-slate-50 px-2 flex items-center text-xs text-slate-400">{texts.email_placeholder}</div>
        <div className="text-xs text-slate-500">{texts.password_label || "Password"}</div>
        <div className="h-9 rounded-md border bg-slate-50 px-2 flex items-center text-xs text-slate-400">{texts.password_placeholder}</div>
        <div className="h-9 rounded-md text-white text-sm flex items-center justify-center mt-2" style={{ background: branding.login_button_color || "#2563EB" }}>{texts.signin_button_text || "Sign in"}</div>
        {providers.map((p) => <div key={p} className="h-8 rounded-md border text-xs flex items-center justify-center text-slate-600">{(texts.oauth_button_text || "Continue with {provider}").replace("{provider}", p === "oidc" ? "SSO" : p)}</div>)}
        <div className="text-xs text-blue-600 text-center pt-1">{texts.forgot_password_text || "Forgot password?"}</div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, Icon]) => (
          <Button key={d} size="sm" variant={device === d ? "default" : "outline"} onClick={() => setDevice(d)} data-testid={`preview-${d}`}><Icon size={15} className="mr-1.5" />{d[0].toUpperCase() + d.slice(1)}</Button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => setDevice("desktop")} data-testid="preview-reset" className="ml-auto"><RefreshCw size={14} className="mr-1.5" />Reset</Button>
      </div>
      <div className="bg-slate-100 rounded-xl p-4 flex justify-center">
        <div className="bg-white rounded-lg overflow-hidden shadow-sm border transition-all" style={{ width: widths[device], maxWidth: "100%" }} data-testid="preview-frame">
          <div className={`flex ${stack ? "flex-col" : ""}`} style={{ minHeight: 380 }}>
            {panelRight ? <>{BrandPanel}{FormPanel}</> : <>{FormPanel}{BrandPanel}</>}
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2">Live preview updates instantly from your unsaved changes. Unsaved preview does not affect the production login page.</p>
    </div>
  );
}

// ---------------- CRM Sync ----------------
function CrmSync({ crm, setCrm, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [sync, setSync] = useState({ status: "idle", detail: "", record_id: null });
  const [logs, setLogs] = useState([]);
  const set = (k, v) => setCrm((c) => ({ ...c, [k]: v }));

  const loadLogs = useCallback(() => { api.get("/branding/crm/logs").then((r) => setLogs(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const addRow = () => set("field_mappings", [...(crm.field_mappings || []), { source: "product_name", crm_field: "", required: false, default: "", transform: "none", active: true }]);
  const setRow = (i, k, v) => { const m = [...(crm.field_mappings || [])]; m[i] = { ...m[i], [k]: v }; set("field_mappings", m); };
  const delRow = (i) => set("field_mappings", (crm.field_mappings || []).filter((_, x) => x !== i));

  const saveCrm = async () => {
    setSaving(true);
    try {
      const payload = { ...crm }; if (!payload.api_key) delete payload.api_key; delete payload.has_api_key;
      const r = await api.put("/branding/crm", payload); setCrm(r.data); toast.success("CRM settings saved"); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const doSync = async (force = false) => {
    setSync({ status: "validating", detail: "Validating…" });
    try {
      // ensure latest config saved first
      const payload = { ...crm }; if (!payload.api_key) delete payload.api_key; delete payload.has_api_key;
      await api.put("/branding/crm", payload);
      setSync({ status: "syncing", detail: "Syncing to CRM…" });
      const r = await api.post("/branding/crm/sync", { force });
      const d = r.data;
      if (d.status === "needs_confirmation") {
        if (window.confirm(`${d.detail}`)) return doSync(true);
        setSync({ status: "idle", detail: "" }); return;
      }
      setSync(d);
      loadLogs();
      if (d.status === "success") toast.success("CRM sync successful"); else toast.error(d.detail || "Sync failed");
    } catch (e) {
      const detail = e.response?.data?.detail || "Sync failed";
      setSync({ status: "failed", detail });
      toast.error(detail);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">CRM Connection</h3>
          <div className="flex items-center gap-2"><span className="text-sm text-slate-500">Enabled</span><Switch checked={!!crm.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="crm-enabled" /></div>
        </div>
        <Field label="Base URL"><Input placeholder="https://api.yourcrm.com" value={crm.base_url || ""} onChange={(e) => set("base_url", e.target.value)} data-testid="crm-base-url" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Endpoint"><Input placeholder="/v1/records" value={crm.endpoint || ""} onChange={(e) => set("endpoint", e.target.value)} data-testid="crm-endpoint" /></Field>
          <Field label="HTTP Method">
            <Select value={crm.http_method} onValueChange={(v) => set("http_method", v)}>
              <SelectTrigger data-testid="crm-method"><SelectValue /></SelectTrigger>
              <SelectContent>{["POST", "PUT", "PATCH"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Auth Type">
            <Select value={crm.auth_type} onValueChange={(v) => set("auth_type", v)}>
              <SelectTrigger data-testid="crm-auth-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem><SelectItem value="bearer">Bearer Token</SelectItem><SelectItem value="api_key">API Key Header</SelectItem></SelectContent>
            </Select>
          </Field>
          {crm.auth_type === "api_key" && <Field label="API Key Header"><Input value={crm.api_key_header || ""} onChange={(e) => set("api_key_header", e.target.value)} data-testid="crm-key-header" /></Field>}
        </div>
        {crm.auth_type !== "none" && (
          <Field label={crm.has_api_key ? "API Key / Token (saved — leave blank to keep)" : "API Key / Token"}>
            <Input type="password" placeholder={crm.has_api_key ? "••••••••" : ""} value={crm.api_key || ""} onChange={(e) => set("api_key", e.target.value)} data-testid="crm-api-key" />
          </Field>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Field label="Timeout (s)"><Input type="number" value={crm.timeout || ""} onChange={(e) => set("timeout", Number(e.target.value))} data-testid="crm-timeout" /></Field>
          <Field label="Retries (≤3)"><Input type="number" value={crm.retry_count ?? ""} onChange={(e) => set("retry_count", Number(e.target.value))} data-testid="crm-retry" /></Field>
          <Field label="Duplicate">
            <Select value={crm.duplicate_check} onValueChange={(v) => set("duplicate_check", v)}>
              <SelectTrigger data-testid="crm-dup"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="update">Update Existing</SelectItem><SelectItem value="ask">Ask User</SelectItem><SelectItem value="stop">Stop</SelectItem><SelectItem value="off">Off</SelectItem></SelectContent>
            </Select>
          </Field>
        </div>
      </div>

      <div className="bg-white border rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Field Mapping</h3>
          <Button size="sm" variant="outline" onClick={addRow} data-testid="crm-add-mapping"><Plus size={14} className="mr-1" />Add</Button>
        </div>
        <div className="space-y-2 max-h-72 overflow-auto">
          {(crm.field_mappings || []).length === 0 && <p className="text-sm text-slate-400">No mappings yet. Add at least one.</p>}
          {(crm.field_mappings || []).map((m, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`crm-map-${i}`}>
              <div className="col-span-4">
                <Select value={m.source} onValueChange={(v) => setRow(i, "source", v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Source" /></SelectTrigger>
                  <SelectContent>{SOURCE_FIELDS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-4"><Input className="h-9" placeholder="CRM field" value={m.crm_field} onChange={(e) => setRow(i, "crm_field", e.target.value)} /></div>
              <div className="col-span-2 flex items-center gap-1 justify-center"><Switch checked={!!m.required} onCheckedChange={(v) => setRow(i, "required", v)} /><span className="text-[10px] text-slate-400">req</span></div>
              <div className="col-span-1 flex items-center justify-center"><Switch checked={m.active !== false} onCheckedChange={(v) => setRow(i, "active", v)} /></div>
              <button className="col-span-1 text-slate-400 hover:text-red-600" onClick={() => delRow(i)}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 flex items-center gap-1"><Link2 size={12} /> Maps Task Matrix fields → your CRM fields. Required mappings must have a source or default.</p>
      </div>

      <div className="lg:col-span-2 flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={saveCrm} disabled={saving} data-testid="save-crm-btn"><Save size={15} className="mr-1.5" />{saving ? "Saving…" : "Save Configuration"}</Button>
        <Button onClick={() => doSync(false)} disabled={["validating", "syncing", "checking"].includes(sync.status)} data-testid="crm-sync-btn">
          {["validating", "syncing"].includes(sync.status) ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <RefreshCw size={15} className="mr-1.5" />}
          Save &amp; Sync to CRM
        </Button>
        {sync.status === "success" && <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="crm-sync-status"><CheckCircle2 size={16} /> CRM Sync Successful{sync.record_id ? ` · Record ${sync.record_id}` : ""}</div>}
        {sync.status === "failed" && <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="crm-sync-status"><XCircle size={16} /> Sync Failed: {sync.detail} <button className="underline" onClick={() => doSync(false)}>Retry</button></div>}
        {["validating", "syncing"].includes(sync.status) && <span className="text-sm text-slate-500" data-testid="crm-sync-status">{sync.detail}</span>}
      </div>

      {logs.length > 0 && (
        <div className="lg:col-span-2 bg-white border rounded-xl p-4">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Recent Sync Activity</h4>
          <div className="divide-y text-sm">
            {logs.slice(0, 8).map((l) => (
              <div key={l.id} className="flex items-center justify-between py-1.5">
                <span className={l.status === "success" ? "text-emerald-700" : "text-red-600"}>{l.status}</span>
                <span className="text-slate-500 truncate max-w-md px-2">{l.detail}</span>
                <span className="text-xs text-slate-400">{new Date(l.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
